import { Box } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';

const MotionBox = motion.create(Box);
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useFeatureFlagStatus } from '@/hooks/use-feature-flag';
import { FeatureFlag } from '@fluenceur/types';
import { usePlanFeaturesQuery } from '@/hooks/queries/use-plan-features-query';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { isFullBleedPath, isPrimaryMenuShown } from '../stores/nav-store';
import GlobalContactDrawer from './contact/GlobalContactDrawer';
import { MetaSubscriptionConversion } from './analytics/MetaSubscriptionConversion';
import { LegalAcceptanceBanner } from './legal';
import { SuccessFeeTermsBanner } from './legal/success-fee-terms';
import GlobalMentionPreviewModal from './mentions/GlobalMentionPreviewModal';
import Loader from './page-loader';
import NavFrameBackdrop, { NAV_FRAME_RADIUS } from './scaffold/layout/NavFrameBackdrop';
import { useIsMobileLayout } from './scaffold/layout/nav-dark';
import TopBar, { TOP_BAR_HEIGHT } from './scaffold/layout/TopBar';
import PrimarySidebar, { SIDEBAR_WIDTH } from './scaffold/sidebar/PrimarySidebar';
import { BillingLimitedSidebar } from './scaffold/sidebar/BillingLimitedSidebar';
import PlatformIncidentBanner from './announcements/PlatformIncidentBanner';
import { FeatureAnnouncementHost } from './announcements/FeatureAnnouncementHost';
import { ImpersonationBanner } from './ImpersonationBanner';
import { PaymentFailureBanner } from './subscription/PaymentFailureBanner';
import BrandInboxRealtimeBridge from './messaging/BrandInboxRealtimeBridge';
import { CompanionDisplayRightsCtaHost } from './companion/cta/CompanionDisplayRightsCtaHost';
import { CompanionGiftReofferCtaHost } from './companion/cta/CompanionGiftReofferCtaHost';
import { CompanionEmailCtaHost } from './companion/cta/CompanionEmailCtaHost';
import { CompanionInvitationCtaHost } from './companion/cta/CompanionInvitationCtaHost';
import { AgentBlocksTextBridge } from './companion/agent/AgentBlocksTextBridge';
import { CompanionPanelHost } from './companion/feed/CompanionPanelHost';
import { CompanionTransmitBridge } from './companion/feed/CompanionTransmitBridge';
import { COMPANION_PANEL_WIDTH } from './companion/panel/companion-panel-layout';
import { CompanionPanelShellHost } from './companion/panel/CompanionPanelShellHost';
import { useOpenCompanionPanel } from './companion/panel/use-open-companion-panel';
import { AffiliateImportBackgroundMonitorRoot } from '@/pages/affiliate-import/_components/AffiliateImportBackgroundMonitor';

function LayoutPrefetch() {
  useSubscriptionQuery();
  usePlanFeaturesQuery();
  return null;
}

type CompanionPanelSlotProps = React.ComponentProps<typeof CompanionPanelHost>;

/**
 * The conversational panel for the `COMPANION_PANEL` cohort (which hands back
 * to the feed panel when the server closes it), the feed panel for everyone
 * else, and nothing until the flag is known.
 */
function CompanionPanelSlot(props: CompanionPanelSlotProps) {
  const { enabled, isLoading } = useFeatureFlagStatus(FeatureFlag.COMPANION_PANEL);
  if (isLoading) return null;
  if (!enabled) return <CompanionPanelHost {...props} />;
  return (
    <AgentBlocksTextBridge>
      <CompanionPanelShellHost {...props} />
    </AgentBlocksTextBridge>
  );
}

interface LayoutProps {
  billingLimited?: boolean;
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ billingLimited = false, children }) => {
  const location = useLocation();
  const isMobile = useIsMobileLayout();
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const { mode: panelMode } = useOpenCompanionPanel();
  // Full-bleed routes (flow editor) hide the whole chrome — derived from the
  // URL only, so there is no imperative flag racing the route change.
  const shouldHidePrimary = isFullBleedPath(location.pathname);

  // Measure banner heights dynamically via refs
  const bannerContainerRef = useRef<HTMLDivElement>(null);
  const [bannerHeight, setBannerHeight] = useState(0);

  const measureBannerHeight = useCallback(() => {
    if (bannerContainerRef.current) {
      setBannerHeight(bannerContainerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    measureBannerHeight();
    const observer = new ResizeObserver(measureBannerHeight);
    if (bannerContainerRef.current) {
      observer.observe(bannerContainerRef.current);
    }
    return () => observer.disconnect();
  }, [measureBannerHeight]);
  const topBarHeight = shouldHidePrimary ? 0 : TOP_BAR_HEIGHT;
  const totalTopOffset = bannerHeight + topBarHeight;
  // Flush content panel: no gutter. Where the dark top bar and menu meet, the
  // white content rounds its corner into the frame (see NavFrameBackdrop).
  const contentGap = 0;
  const showMenu = isPrimaryMenuShown(isMobile, location.pathname);

  // Single fixed sidebar: the content starts at its right edge. The internal
  // Settings/Conversations/Program panels slide inside that same rail, so the
  // content margin never changes with the active panel.
  const contentLeft = showMenu ? SIDEBAR_WIDTH : 0;

  // The conversational panel: pushed, it takes its width from the content's
  // right; full screen, everything behind it leaves the keyboard and the
  // accessibility tree. The shell is never mounted on a full-bleed route.
  const panelShown = !billingLimited && !shouldHidePrimary;
  const panelPushes = panelShown && panelMode === 'push';
  const behindModalPanel = panelShown && panelMode === 'fullscreen' ? true : undefined;

  return (
    <>
      {!billingLimited && (
        <>
          <LayoutPrefetch />
          <AffiliateImportBackgroundMonitorRoot />
        </>
      )}
      {/* Banners - measured dynamically to offset layout */}
      <Box
        ref={bannerContainerRef}
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex="1300"
        inert={behindModalPanel}
      >
        {!billingLimited && <PlatformIncidentBanner brandId={user?.currentBrandId ?? null} />}
        <ImpersonationBanner />
        <MetaSubscriptionConversion />
        <LegalAcceptanceBanner key={user?.id} />
        {!billingLimited && (
          <>
            <PaymentFailureBanner />
            <SuccessFeeTermsBanner />
          </>
        )}
      </Box>

      {/* Dark frame behind the top bar and the menu */}
      {!shouldHidePrimary && <NavFrameBackdrop top={bannerHeight} />}

      {/* Top Navigation Bar - Below banner if visible */}
      {!shouldHidePrimary && (
        <Box display="contents" inert={behindModalPanel}>
          <TopBar top={`${bannerHeight}px`} actions={billingLimited ? null : undefined} />
        </Box>
      )}

      {/* Sidebar Navigation */}
      {!shouldHidePrimary && (
        <Box display="contents" inert={behindModalPanel}>
          {billingLimited ? (
            <BillingLimitedSidebar bannerHeight={bannerHeight} />
          ) : (
            <PrimarySidebar bannerHeight={bannerHeight} />
          )}
        </Box>
      )}

      {/* Main Content */}
      <MotionBox
        as="main"
        position="fixed"
        top={`${totalTopOffset + contentGap}px`}
        bottom={`${contentGap}px`}
        overflow="auto"
        bg="white"
        borderTopLeftRadius={showMenu ? `${NAV_FRAME_RADIUS}px` : 0}
        borderTopRightRadius={panelPushes ? `${NAV_FRAME_RADIUS}px` : 0}
        inert={behindModalPanel}
        animate={{ left: contentLeft, right: panelPushes ? COMPANION_PANEL_WIDTH : contentGap }}
        transition={{
          left:
            shouldHidePrimary || reduceMotion
              ? { duration: 0 }
              : { type: 'spring', stiffness: 300, damping: 35 },
          // A spring on the right edge would reflow heavy grids on every frame.
          right: { duration: 0 },
        }}
      >
        {billingLimited ? (
          children
        ) : (
          <>
            <Loader />
            <Outlet />
          </>
        )}
      </MotionBox>

      {!billingLimited && (
        <>
          <GlobalContactDrawer />
          <GlobalMentionPreviewModal />
          <BrandInboxRealtimeBridge brandId={user?.currentBrandId} />
          <CompanionTransmitBridge brandId={user?.currentBrandId} />
          <CompanionPanelSlot
            userId={user?.id}
            brandId={user?.currentBrandId}
            topOffset={totalTopOffset}
            hidden={shouldHidePrimary}
          />
          {/* Outside the panel on purpose: the panel closes when a card hands the
              brand over to the composer, and a modal it owned would close with it. */}
          <CompanionEmailCtaHost />
          <CompanionInvitationCtaHost />
          <CompanionDisplayRightsCtaHost />
          <CompanionGiftReofferCtaHost />

          {!shouldHidePrimary && <FeatureAnnouncementHost brandId={user?.currentBrandId ?? null} />}
        </>
      )}
    </>
  );
};

export default Layout;
